import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { getRouteKeyFromPath, getTranslatedUrl } from "@/lib/route-mapping";
import { getComprehensiveSEOData } from "@/lib/comprehensive-seo-data";
import { seoDataI18n } from "@/lib/seo-data-i18n";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  schema?: object | object[];
  noindex?: boolean;
  // Control flags
  useMapping?: boolean; // If true, override with CSV mapping
}

const SEO = ({
  title: propTitle,
  description: propDescription,
  keywords,
  canonical,
  ogImage = "https://danielalbertlaw.com/og-image.jpg",
  ogType = "website",
  schema,
  noindex = false,
  useMapping = true,
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = "https://danielalbertlaw.com";
  const { language } = useLanguage();

  // Normalize pathname (remove trailing slash except root)
  const normalizedPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '');
  
  // Get comprehensive SEO data from our complete mapping
  const seoData = getComprehensiveSEOData(normalizedPath, language);
  
  // Merge with i18n mapping when available (ensures better uniqueness per URL/lang)
  const i18n = seoDataI18n[normalizedPath]?.[language];

  // Priority: 1) Props 2) i18n mapping 3) Comprehensive fallback
  const baseTitle = propTitle || i18n?.title || seoData.title;
  const description = propDescription || i18n?.description || seoData.description;
  
  // Avoid duplicating brand name in title
  const fullTitle = baseTitle.includes("Daniel Albert Law") ? baseTitle : `${baseTitle} | Daniel Albert Law Firm`;
  
  // Get route key and build alternate URLs
  const routeKey = getRouteKeyFromPath(normalizedPath);
  const enUrl = routeKey ? `${siteUrl}${getTranslatedUrl(routeKey, 'en')}` : `${siteUrl}${normalizedPath}`;
  const esUrl = routeKey ? `${siteUrl}${getTranslatedUrl(routeKey, 'es')}` : `${siteUrl}/es${normalizedPath}`;
  const viUrl = routeKey ? `${siteUrl}${getTranslatedUrl(routeKey, 'vi')}` : `${siteUrl}/vi${normalizedPath}`;
  
  // Current language URL (CRITICAL: This is the canonical URL for THIS page)
  const currentLangUrl = language === 'es' ? esUrl : language === 'vi' ? viUrl : enUrl;
  
  // CRITICAL: canonical should always be the normalized current page URL (no trailing slash)
  // Use provided canonical if available, otherwise use current page URL
  const canonicalUrl = canonical || currentLangUrl;
  
  // Check if admin page
  const isAdminPage = location.pathname.startsWith('/admin');
  const shouldNoindex = noindex || isAdminPage;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta httpEquiv="content-language" content={language} />
      
      {/* Robots - Default to index, follow unless noindex or admin */}
      <meta name="robots" content={shouldNoindex ? "noindex, nofollow" : "index, follow"} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={language === 'es' ? 'es_ES' : language === 'vi' ? 'vi_VN' : 'en_US'} />
      {language !== 'es' && <meta property="og:locale:alternate" content="es_ES" />}
      {language !== 'vi' && <meta property="og:locale:alternate" content="vi_VN" />}
      {language !== 'en' && <meta property="og:locale:alternate" content="en_US" />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Performance: Preconnect to external origins */}
      <link rel="preconnect" href="https://www.googletagmanager.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.google-analytics.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://images.unsplash.com" />

      {/* hreflang for multilingual - SEO best practice */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="vi" href={viUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      {/* Schema.org markup */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;